import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  BookOpen, ArrowLeft, ChevronRight, Loader2,
  CheckCircle2, Circle, Lock, Crown
} from 'lucide-react';

// Freemium: First 2 chapters per book are free
const FREE_CHAPTERS_PER_BOOK = 2;

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const FoundationBookPage = () => {
  const { subjectId, bookId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if user is PRO
  const isPro = user?.role === "PRO";

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchChapters();
  }, [user, token, bookId, navigate]);

  const fetchChapters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/foundation/books/${bookId}/chapters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      
      const data = await res.json();
      setBook(data.book);
      setChapters(data.chapters || []);
      setProgress(data.progress);
      
    } catch (error) {
      console.error('Error fetching chapters:', error);
      toast.error('Failed to load chapters');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <p className="text-slate-500">Book not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="foundation-book-page">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <Link 
              to={`/foundation/${subjectId}`} 
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <span className="px-3 py-1 bg-white/20 text-white text-sm font-medium rounded-full">
                Class {book.class_num}
              </span>
              <h1 className="text-2xl font-bold font-heading mt-2">
                {book.title}
              </h1>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-emerald-100">Reading Progress</span>
              <span className="text-white font-medium">
                {progress?.completed || 0}/{progress?.total || chapters.length} completed
              </span>
            </div>
            <Progress value={progress?.percentage || 0} className="h-2 bg-white/20" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Freemium Banner for Free Users */}
        {!isPro && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Crown className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">
                  Free: First {FREE_CHAPTERS_PER_BOOK} chapters • Upgrade for full access
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {chapters.length - FREE_CHAPTERS_PER_BOOK} more chapters available with PRO
                </p>
              </div>
              <Link to="/upgrade">
                <Badge className="bg-amber-500 hover:bg-amber-600 text-white cursor-pointer">
                  Upgrade
                </Badge>
              </Link>
            </div>
          </div>
        )}

        {/* Chapters List */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-700">Chapters</h2>
          
          {chapters.map((chapter, index) => {
            const isLocked = !isPro && index >= FREE_CHAPTERS_PER_BOOK;
            
            if (isLocked) {
              return (
                <div 
                  key={chapter.id}
                  onClick={() => {
                    toast.error('Upgrade to PRO to access this chapter', {
                      action: {
                        label: 'Upgrade',
                        onClick: () => navigate('/upgrade')
                      }
                    });
                  }}
                  data-testid={`foundation-chapter-${chapter.id}-locked`}
                >
                  <Card className="border border-slate-200 bg-slate-50 cursor-pointer hover:border-amber-300 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Chapter Number */}
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 bg-slate-200 text-slate-400">
                          {chapter.num}
                        </div>
                        
                        {/* Chapter Info */}
                        <div className="flex-1 min-w-0 opacity-60">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-medium text-slate-500">
                              {chapter.title}
                            </h3>
                            <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {chapter.topics.slice(0, 2).map((topic, idx) => (
                              <span 
                                key={idx}
                                className="px-2 py-0.5 bg-slate-200 text-slate-400 text-xs rounded"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {/* PRO Badge */}
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                          <Crown className="h-3 w-3 mr-1" />
                          PRO
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            }
            
            return (
              <Link 
                key={chapter.id} 
                to={`/foundation/${subjectId}/book/${bookId}/chapter/${chapter.id}`}
                data-testid={`foundation-chapter-${chapter.id}`}
              >
                <Card className={`border transition-all cursor-pointer group ${
                  chapter.completed 
                    ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-300' 
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Chapter Number */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        chapter.completed 
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {chapter.num}
                      </div>
                      
                      {/* Chapter Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-medium text-slate-900 group-hover:text-emerald-600 transition-colors">
                            {chapter.title}
                          </h3>
                          {chapter.completed && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          )}
                          {index < FREE_CHAPTERS_PER_BOOK && !isPro && (
                            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-600">FREE</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {chapter.topics.slice(0, 3).map((topic, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded"
                            >
                              {topic}
                            </span>
                          ))}
                          {chapter.topics.length > 3 && (
                            <span className="text-slate-400 text-xs">
                              +{chapter.topics.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FoundationBookPage;
